import { GoogleLogo, X, Crown, DownloadSimple, Sparkle, FloppyDisk, Export } from "@phosphor-icons/react";

/**
 * Lazy Registration modal — shown to guests who've hit an auth gate in AI Pro
 * Studio. Supported triggers:
 *   • download          — user clicked Download on their guest render
 *   • save              — user clicked Save to Workspace / Save to Library
 *   • export            — user clicked Open in FlyerStudio Editor
 *   • second_generate   — user clicked Rerun or Generate a second time
 *
 * Triggers the same Emergent Google OAuth flow used everywhere else;
 * AuthCallback.jsx handles guest_id handoff and lands the user back at
 * /ai-pro-studio?resume=1 so the pending action executes automatically.
 *
 * The modal itself does NOT authenticate directly — it hands off to the
 * Emergent OAuth URL, which returns via hash → AuthCallback → /auth/session.
 * `pendingAction` is persisted in localStorage BEFORE redirecting so it
 * survives the OAuth round-trip.
 */
const COPY = {
  download: {
    icon: DownloadSimple,
    headline: "Create your free account to download this masterpiece and unlock 9 more free credits!",
  },
  save: {
    icon: FloppyDisk,
    headline: "Save this design to your account — create a free account and unlock 9 more free credits!",
  },
  export: {
    icon: Export,
    headline: "Export this design in the FlyerStudio Editor — create a free account and unlock 9 more free credits!",
  },
  second_generate: {
    icon: Sparkle,
    headline: "You've used your free guest credit! Create your free account to save this design and unlock 9 more free credits!",
  },
};

export default function GuestAuthModal({ open, trigger, onClose, pendingAction }) {
  if (!open) return null;

  const variant = COPY[trigger] || COPY.second_generate;
  const Icon = variant.icon;

  const handleGoogle = () => {
    try {
      if (pendingAction) {
        localStorage.setItem("flyerstudio_guest_pending_action", JSON.stringify(pendingAction));
      }
    } catch (_) { /* no-op */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/ai-pro-studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      data-testid="guest-auth-modal"
      data-trigger={trigger || ""}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0E0E0E] p-7 shadow-2xl text-center">
        <button
          data-testid="guest-auth-modal-close"
          onClick={onClose}
          className="absolute top-5 right-5 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="mx-auto h-14 w-14 rounded-2xl bg-fuchsia-500/15 ring-1 ring-fuchsia-400/30 flex items-center justify-center mb-4">
          <Icon weight="fill" size={26} className="text-fuchsia-300" />
        </div>

        <p
          data-testid="guest-auth-modal-headline"
          className="font-display font-black text-xl tracking-tight mb-5 leading-tight"
        >
          {variant.headline}
        </p>

        <button
          data-testid="guest-auth-modal-google"
          onClick={handleGoogle}
          className="w-full inline-flex items-center justify-center gap-3 h-12 rounded-full bg-white text-black font-semibold hover:bg-zinc-200 transition-colors"
        >
          <GoogleLogo weight="bold" size={20} /> Continue with Google
        </button>

        <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-zinc-400">
          <Crown weight="fill" size={13} style={{ color: "#FFD60A" }} />
          <span>9 free credits will be added instantly · no card required</span>
        </div>
      </div>
    </div>
  );
}
